/**
 * PostDiscussionArtifacts
 *
 * Shown to BYOD participants after a discussion ends.
 * Two phases:
 *   1. Speaker Claiming — participants rename diarization labels to real names,
 *      then confirm to finalize the transcript.
 *   2. Artifact Review — collaboration assessment + concept map shown read-only
 *      once generated.
 */

import React, { useState, useEffect, useCallback } from 'react';
import SevenCsPanel from '../seven-cs/SevenCsPanel';
import ConceptMapView from '../concept-map/ConceptMapView';
import DiscussionPulsePanel from '../discussion-pulse';
import TranscriptPanel from '../transcript-drawer/TranscriptDrawer';
import { scopedFetch } from '../../services/scoped-fetch';

// ─── Speaker Claiming Phase ──────────────────────────────────────────────────

const SpeakerClaimingView = ({ sessionDeviceId, sessionId, onFinalized, socket, canFinalize = true }) => {
    const [speakers, setSpeakers] = useState([]);
    const [transcripts, setTranscripts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [finalizing, setFinalizing] = useState(false);

    // Fetch speakers and transcripts (scoped-key access via sessionStorage)
    useEffect(() => {
        if (!sessionDeviceId) return;
        scopedFetch(`/api/v1/devices/${sessionDeviceId}/speakers`)
            .then(r => r.ok ? r.json() : [])
            .then(data => setSpeakers(Array.isArray(data) ? data : []))
            .catch(() => {});
        scopedFetch(`/api/v1/devices/${sessionDeviceId}/transcripts/client`)
            .then(r => r.ok ? r.json() : [])
            .then(data => setTranscripts(Array.isArray(data) ? data.sort((a, b) => a.start_time - b.start_time) : []))
            .catch(() => {});
    }, [sessionDeviceId]);

    // Live sync: listen for speaker_renamed broadcasts so edits by viewers
    // show up immediately in the recorder's list + transcript. Also
    // re-emit join_group_room on mount: the byod-join-component's main
    // useEffect already joined the group room earlier, but if the socket
    // had to reconnect in the meantime, or if this component mounted
    // after that effect's cleanup ran, we need to ensure room membership
    // explicitly. join_room is idempotent on the server side.
    useEffect(() => {
        if (!socket || !sessionDeviceId) return;
        try { socket.emit('join_group_room', { session_device_id: sessionDeviceId }); } catch (e) {}
        const onReconnect = () => {
            try { socket.emit('join_group_room', { session_device_id: sessionDeviceId }); } catch (e) {}
        };
        socket.on('connect', onReconnect);
        const onRenamed = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sessionDeviceId) return;
                setSpeakers(prev => prev.map(s =>
                    s.id === data.speaker_id ? { ...s, alias: data.alias } : s
                ));
                setTranscripts(prev => prev.map(t =>
                    t.speaker_id === data.speaker_id ? { ...t, speaker_tag: data.alias } : t
                ));
            } catch (e) {}
        };
        socket.on('speaker_renamed', onRenamed);
        return () => {
            socket.off('speaker_renamed', onRenamed);
            socket.off('connect', onReconnect);
        };
    }, [socket, sessionDeviceId]);

    const handleRename = (speakerId) => {
        if (!editValue.trim()) return;
        scopedFetch(`/api/v1/devices/${sessionDeviceId}/speakers/${speakerId}/rename`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ alias: editValue.trim() }),
        })
            .then(r => r.ok ? r.json() : null)
            .then(updated => {
                if (updated) {
                    setSpeakers(prev => prev.map(s => s.id === speakerId ? { ...s, alias: updated.alias } : s));
                    // Update transcript display
                    setTranscripts(prev => prev.map(t =>
                        t.speaker_id === speakerId ? { ...t, speaker_tag: updated.alias } : t
                    ));
                }
                setEditing(null);
                setEditValue('');
            })
            .catch(() => setEditing(null));
    };

    const handleFinalize = () => {
        setFinalizing(true);
        scopedFetch(`/api/v1/devices/${sessionDeviceId}/finalize-transcript`, { method: 'POST' })
            .then(r => r.json())
            .then(() => onFinalized())
            .catch(() => onFinalized());
    };

    const formatTime = (seconds) => {
        if (seconds == null) return '';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    // Count utterances per speaker (by speaker_id, which is stable across renames)
    const speakerCounts = {};
    transcripts.forEach(t => {
        if (t.speaker_id) {
            speakerCounts[t.speaker_id] = (speakerCounts[t.speaker_id] || 0) + 1;
        }
    });

    return (
        <div>
            {/* Instructions */}
            <div style={{
                padding: '16px 20px', background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: '8px', marginBottom: '20px',
            }}>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#1e40af', fontWeight: 500 }}>
                    Claim your speech
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#3b82f6' }}>
                    Click on a speaker name below to set the real name. The change applies to all
                    utterances from that speaker. When done, click "Confirm & Finalize" to generate
                    your collaboration assessment and concept map.
                </p>
            </div>

            {/* Speaker list with rename controls */}
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px',
            }}>
                {speakers.map(s => {
                    const isAgent = s.alias === 'Blinc';
                    return (
                    <div key={s.id} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '6px 12px', background: isAgent ? '#f0fdf4' : '#fff',
                        border: `1px solid ${isAgent ? '#bbf7d0' : '#e2e8f0'}`,
                        borderRadius: '8px', fontSize: '0.84rem',
                    }}>
                        {isAgent ? (
                            <>
                                <span style={{ fontWeight: 500, color: '#16a34a' }}>
                                    {s.alias}
                                </span>
                                <span style={{ color: '#86efac', fontSize: '0.72rem' }}>
                                    (facilitator)
                                </span>
                            </>
                        ) : editing === s.id ? (
                            <>
                                <input
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRename(s.id)}
                                    autoFocus
                                    maxLength={64}
                                    placeholder="Your name"
                                    style={{
                                        width: '120px', padding: '3px 8px', border: '1px solid #a5b4fc',
                                        borderRadius: '4px', fontSize: '0.82rem', outline: 'none',
                                    }}
                                />
                                <button
                                    onClick={() => handleRename(s.id)}
                                    style={{
                                        padding: '3px 10px', background: '#6366f1', color: '#fff',
                                        border: 'none', borderRadius: '4px', fontSize: '0.75rem',
                                        cursor: 'pointer', fontWeight: 500,
                                    }}
                                >Save</button>
                                <button
                                    onClick={() => { setEditing(null); setEditValue(''); }}
                                    style={{
                                        background: 'none', border: 'none', color: '#94a3b8',
                                        cursor: 'pointer', fontSize: '0.82rem',
                                    }}
                                >x</button>
                            </>
                        ) : (
                            <>
                                <span style={{ fontWeight: 500, color: '#334155' }}>
                                    {s.alias}
                                </span>
                                <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                                    ({speakerCounts[s.id] || 0} utterances)
                                </span>
                                <button
                                    onClick={() => { setEditing(s.id); setEditValue(s.alias || ''); }}
                                    style={{
                                        background: 'none', border: 'none', color: '#6366f1',
                                        cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                                    }}
                                >Rename</button>
                            </>
                        )}
                    </div>
                    );
                })}
            </div>

            {/* Transcript preview */}
            <div style={{
                maxHeight: '300px', overflow: 'auto', background: '#fff',
                border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '16px',
            }}>
                {transcripts.map((t, i) => (
                    <div key={t.id || i} style={{
                        padding: '6px 12px', borderBottom: '1px solid #f1f5f9',
                        fontSize: '0.82rem',
                    }}>
                        <span style={{
                            fontWeight: 600,
                            color: t.speaker_tag === 'Blinc' ? '#16a34a' : '#6366f1',
                            fontSize: '0.72rem',
                            textTransform: 'uppercase', letterSpacing: '0.03em',
                            cursor: t.speaker_tag === 'Blinc' ? 'default' : 'pointer',
                        }}
                            onClick={() => {
                                if (t.speaker_tag === 'Blinc') return;
                                const speaker = speakers.find(s => s.alias === t.speaker_tag || s.id === t.speaker_id);
                                if (speaker) { setEditing(speaker.id); setEditValue(speaker.alias || ''); }
                            }}
                        >
                            {t.speaker_tag || 'Unknown'}
                        </span>
                        <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: '6px' }}>
                            {formatTime(t.start_time)}
                        </span>
                        <p style={{ margin: '2px 0 0', color: '#334155', lineHeight: 1.4 }}>
                            {t.transcript}
                        </p>
                    </div>
                ))}
            </div>

            {/* Confirm button — only the recorder can finalize. Viewers see a
                waiting message instead; they get auto-transitioned to the
                artifacts view via the session_finalized SocketIO event. */}
            {canFinalize ? (
                <>
                    <button
                        onClick={handleFinalize}
                        disabled={finalizing}
                        style={{
                            width: '100%', padding: '12px', background: finalizing ? '#94a3b8' : '#16a34a',
                            color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.92rem',
                            fontWeight: 600, cursor: finalizing ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                        }}
                    >
                        {finalizing ? 'Finalizing...' : 'Confirm & Finalize Transcript'}
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#94a3b8', marginTop: '6px' }}>
                        Transcript will auto-finalize in 5 minutes if not confirmed
                    </p>
                </>
            ) : (
                <div style={{
                    textAlign: 'center', padding: '14px', background: '#f9fafb',
                    borderRadius: 8, color: '#6b7280', fontSize: '0.85rem',
                    border: '1px dashed #d1d5db',
                }}>
                    Waiting for the recorder to finalize…
                </div>
            )}
        </div>
    );
};


// ─── Main Component ──────────────────────────────────────────────────────────

const PostDiscussionArtifacts = ({ sessionDeviceId, sessionId, speakerMode, onClose, socket, canFinalize = true }) => {
    // Mode A (auto): show speaker claiming first. Mode B (enrolled): skip to artifacts.
    const [phase, setPhase] = useState(speakerMode === 'auto' ? 'claiming' : 'artifacts');
    const [assessmentReady, setAssessmentReady] = useState(false);
    const [conceptsReady, setConceptsReady] = useState(false);
    // Distinct from "ready" because a failed analysis (e.g., zero transcripts)
    // ALSO terminates polling but shouldn't mount SevenCsPanel — the panel
    // expects a populated summary and renders weirdly without one.
    const [assessmentFailed, setAssessmentFailed] = useState(false);
    const [conceptsFailed, setConceptsFailed] = useState(false);

    const checkStatus = useCallback(async () => {
        if (!sessionDeviceId) return;
        try {
            const res = await scopedFetch(`/api/v1/seven-cs/results/${sessionDeviceId}`);
            if (res.ok) {
                const data = await res.json();
                if (data.status === 'completed') {
                    setAssessmentReady(true);
                    setAssessmentFailed(false);
                } else if (data.status === 'failed') {
                    setAssessmentFailed(true);
                    setAssessmentReady(false);
                }
                // 'not_analyzed' or 'processing' → keep polling, leave both flags as-is.
            }
        } catch (_) {}
        try {
            const res = await scopedFetch(`/api/v1/concepts/${sessionDeviceId}`);
            if (res.ok) {
                const data = await res.json();
                if (data.nodes && data.nodes.length > 0) {
                    setConceptsReady(true);
                    setConceptsFailed(false);
                } else if (data.generation_status === 'failed' || data.generation_error) {
                    setConceptsFailed(true);
                    setConceptsReady(false);
                }
                // generation_status === 'processing' or unset → keep polling.
            }
        } catch (_) {}
    }, [sessionDeviceId]);

    // Poll for artifacts once in artifacts phase
    useEffect(() => {
        if (phase !== 'artifacts') return;
        checkStatus();
        const interval = setInterval(checkStatus, 5000);
        return () => clearInterval(interval);
    }, [phase, sessionDeviceId, checkStatus]);

    const handleFinalized = () => {
        setPhase('artifacts');
    };

    // Also listen for session_finalized broadcasts so that if the
    // auto-finalize timeout fires (or another device initiated finalize),
    // we transition from claiming → artifacts without the user clicking.
    useEffect(() => {
        if (!socket || !sessionDeviceId) return;
        const onFinalized = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id === sessionDeviceId) {
                    setPhase('artifacts');
                }
            } catch (e) {}
        };
        socket.on('session_finalized', onFinalized);
        return () => socket.off('session_finalized', onFinalized);
    }, [socket, sessionDeviceId]);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: '#f8fafc', zIndex: 1000,
            // Flex column so the content area below the header can use
            // flex: 1 to fill the remaining viewport. Without this, the
            // two-column artifacts grid collapses to content-natural
            // height and the Pulse sidebar (which uses fill=true) has
            // no height to fill.
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0',
                flexShrink: 0,
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: 600 }}>
                        {phase === 'claiming' ? 'Identify Speakers' : 'Discussion Complete'}
                    </h2>
                    <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
                        {phase === 'claiming'
                            ? 'Claim your speech before generating artifacts'
                            : 'Review your group\'s collaboration assessment and concept map'}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        padding: '6px 16px', borderRadius: '6px', border: '1px solid #e2e8f0',
                        background: '#fff', cursor: 'pointer', fontSize: '0.82rem', color: '#64748b',
                    }}
                >
                    Done
                </button>
            </div>

            {/* Content */}
            <div style={{
                flex: 1, padding: '16px 20px', minHeight: 0,
                display: 'flex', flexDirection: 'column',
                // Claiming phase has a long form (transcript review +
                // speaker list + finalize button) that may overflow on
                // small viewports; let it scroll. Artifacts phase fills
                // the available space exactly via inner flex:1 grid, so
                // overflow:auto is a no-op there.
                overflow: 'auto',
            }}>
                {phase === 'claiming' && (
                    <div style={{ maxWidth: '1000px', width: '100%', margin: '0 auto' }}>
                        <SpeakerClaimingView
                            sessionDeviceId={sessionDeviceId}
                            sessionId={sessionId}
                            onFinalized={handleFinalized}
                            socket={socket}
                            canFinalize={canFinalize}
                        />
                    </div>
                )}

                {phase === 'artifacts' && (
                    <ArtifactsLayout
                        sessionDeviceId={sessionDeviceId}
                        sessionId={sessionId}
                        assessmentReady={assessmentReady}
                        conceptsReady={conceptsReady}
                        assessmentFailed={assessmentFailed}
                        conceptsFailed={conceptsFailed}
                    />
                )}
            </div>
        </div>
    );
};

// Two-column artifacts layout: assessment + concept map on the left,
// Discussion Pulse on the right. Transcript is hidden behind a button
// that opens a modal overlay; click backdrop or press Escape to close.
function ArtifactsLayout({ sessionDeviceId, sessionId, assessmentReady, conceptsReady, assessmentFailed, conceptsFailed }) {
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    useEffect(() => {
        if (!transcriptOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') setTranscriptOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [transcriptOpen]);

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                marginBottom: 12,
            }}>
                <button
                    onClick={() => setTranscriptOpen(true)}
                    style={{
                        padding: '6px 14px', background: '#fff', border: '1px solid #93c5fd',
                        borderRadius: 6, color: '#1e40af', fontSize: '0.82rem', fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    📄 Transcript
                </button>
            </div>

            <div style={{
                flex: 1, display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
                gap: 16, minHeight: 0,
            }}>
                {/* Left: assessment + concept map, scrollable */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0, overflow: 'auto', paddingRight: 4 }}>
                    <section>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: '0 0 12px' }}>
                            Collaboration Assessment
                        </h3>
                        {assessmentReady ? (
                            <SevenCsPanel sessionDeviceId={sessionDeviceId} readOnly={true} />
                        ) : assessmentFailed ? (
                            <div style={{
                                padding: '40px', textAlign: 'center', background: '#fff',
                                borderRadius: '8px', border: '1px solid #fecaca',
                            }}>
                                <p style={{ color: '#b91c1c', fontSize: '0.88rem', margin: 0 }}>
                                    Couldn't generate the collaboration assessment.
                                </p>
                                <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Generation failed. Ask your TA to retry, or check the server logs.
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                padding: '40px', textAlign: 'center', background: '#fff',
                                borderRadius: '8px', border: '1px solid #e2e8f0',
                            }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>&#9203;</div>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>
                                    Generating collaboration assessment...
                                </p>
                                <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px' }}>
                                    This usually takes 30-60 seconds
                                </p>
                            </div>
                        )}
                    </section>

                    <section>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: '0 0 12px' }}>
                            Discussion Concept Map
                        </h3>
                        {conceptsReady ? (
                            <ConceptMapView sessionId={sessionId} sessionDeviceId={sessionDeviceId} readOnly={true} />
                        ) : conceptsFailed ? (
                            <div style={{
                                padding: '40px', textAlign: 'center', background: '#fff',
                                borderRadius: '8px', border: '1px solid #fecaca',
                            }}>
                                <p style={{ color: '#b91c1c', fontSize: '0.88rem', margin: 0 }}>
                                    Couldn't generate the concept map.
                                </p>
                                <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Generation failed. Ask your TA to retry, or check the server logs.
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                padding: '40px', textAlign: 'center', background: '#fff',
                                borderRadius: '8px', border: '1px solid #e2e8f0',
                            }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>&#9203;</div>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>
                                    Generating concept map...
                                </p>
                                <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px' }}>
                                    This usually takes 1-2 minutes
                                </p>
                            </div>
                        )}
                    </section>
                </div>

                {/* Right: Discussion Pulse only (no Blinc post-finalize) */}
                <div style={{ minHeight: 0, overflow: 'hidden' }}>
                    <DiscussionPulsePanel
                        sessionDeviceId={sessionDeviceId}
                        isSessionActive={false}
                        fill={true}
                    />
                </div>
            </div>

            {transcriptOpen && (
                <div
                    onClick={() => setTranscriptOpen(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(15, 23, 42, 0.55)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%', maxWidth: 760, height: '85vh',
                            background: '#fff', borderRadius: 10,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}
                    >
                        <TranscriptPanel
                            deviceId={sessionDeviceId}
                            sessionId={sessionId}
                            speakerVersion={0}
                            readOnly={true}
                            onClose={() => setTranscriptOpen(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default PostDiscussionArtifacts;
